import {
  Checkbox,
  Form,
  InputField,
  SelectField,
  UploadImageField,
} from "@/components/Form";
import {
  merchantRegisterSchema,
  MerchantRegisterSchemaModel,
} from "../types/schema";
import { Button, EditIc, Image, MailIc, UserIc } from "@/components/Elements";
import useToast from "@/hooks/useToast";
import {
  useRegisterMerchantUserMutation,
  useVerifyCaptchaTokenMutation,
} from "../api/login";
import { Captcha } from "@/features/misc";
import { PrivacyPolicy } from "./register";
import { RouteConstant } from "@/routes/routeConstant";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/store/reducers";

export const MerchantRegisterForm = () => {
  const [registerUser] = useRegisterMerchantUserMutation();
  const navigate = useNavigate();
  const { showError } = useToast();
  const [token, setToken] = useState<string>("");
  const [verifyCaptchaToken] = useVerifyCaptchaTokenMutation();
  const categoryList = useSelector(
    (state: RootState) => state.appData.appData.merchantBusinessCategoryList
  );

  async function handleRegister({
    profileImage,
    ...rest
  }: MerchantRegisterSchemaModel) {
    const result = await verifyCaptchaToken(token).unwrap();
    if (!result.isValid) {
      showError("Please verify captcha");
      return;
    }
    try {
      const data = await registerUser({
        ...rest,
        profileImage:
          profileImage && profileImage.fileDataAsBase64 ? profileImage : null,
      }).unwrap();
      if (data) {
        navigate(RouteConstant.Login);
      }
    } catch (e) {}
  }

  return (
    <>
      <Form<MerchantRegisterSchemaModel, typeof merchantRegisterSchema>
        onSubmit={async (values) => {
          await handleRegister(values);
        }}
        schema={merchantRegisterSchema}
      >
        {(props) => {
          const { watch, setValue, register, formState } = props;
          const profileImage = watch("profileImage");

          return (
            <>
              <div className="mt-[30px] mb-[20px] flex items-center justify-center">
                <UploadImageField name="profileImage" setValue={setValue}>
                  <div className="border border-[#d3d3d3] rounded-[50px] w-[100px] h-[100px] p-[5px] relative">
                    <Image
                      fileDataAsBase64={profileImage?.fileDataAsBase64}
                      className="self-center w-full h-full rounded-[50px]"
                    />
                    <div className="absolute bottom-0 right-0 z-10">
                      <EditIc className="w-6 h-6 text-[#969fa4]" />
                    </div>
                  </div>
                </UploadImageField>
              </div>
              <InputField
                type="text"
                placeholder="Business Name"
                label="Business Name"
                registration={register("businessName")}
                error={formState.errors["businessName"]}
              />
              <InputField
                type="text"
                placeholder="Business Address"
                label="Business Address"
                registration={register("businessAddress")}
                error={formState.errors["businessAddress"]}
              />
              <InputField
                type="text"
                placeholder="First Name"
                label="First Name"
                registration={register("firstName")}
                error={formState.errors["firstName"]}
                icon={<UserIc className="h-6 w-6" />}
              />
              <InputField
                type="text"
                placeholder="Last Name"
                label="Last Name"
                registration={register("lastName")}
                error={formState.errors["lastName"]}
                icon={<UserIc className="h-6 w-6" />}
              />
              <InputField
                type="text"
                placeholder="Email Address"
                label="Email Address"
                registration={register("email")}
                error={formState.errors["email"]}
                icon={<MailIc className="h-6 w-6" />}
              />
              <InputField
                type="password"
                label="Password"
                error={formState.errors["password"]}
                registration={register("password")}
              />
              <InputField
                type="password"
                label="Confirm Password"
                error={formState.errors["confirmPassword"]}
                registration={register("confirmPassword")}
              />
              <InputField
                type="text"
                placeholder="Website or social media page"
                label="Website or social media page"
                registration={register("websiteUrl")}
              />
              <SelectField
                label="Business Category"
                registration={register("categoryId")}
                options={categoryList}
              />
              <div className="m-[10px]">
                <Checkbox
                  registration={register("isTermsAccepted")}
                  error={formState.errors["isTermsAccepted"]}
                >
                  <PrivacyPolicy />
                </Checkbox>
              </div>
              <div className="p-[10px]">
                <Captcha onVerify={setToken} />
              </div>
              <div className="flex p-[20px]">
                <Button type="submit" className="w-full">
                  Register
                </Button>
              </div>
            </>
          );
        }}
      </Form>
    </>
  );
};
